import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cropper from "react-easy-crop";

import { OthersAction } from "../../store/actions";

import Loader from "../loader";
import { Images, Utils } from "../../config";
import { Modal, Buttons } from "..";

import "./styles.css";

const DefaultFileUpload = ({
  id,
  type,
  value,
  icon = Images.DefaultUpload,
  disabled = false,
  showcrop = false,
  ...props
}) => {
  const dispatch = useDispatch();
  const fileSelect = useRef();

  const { isUploadLoading } = useSelector((state) => state.others);

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const handleFileUpload = (file) => {
    const data = new FormData();

    if (type === "image") {
      data.append("image", file);
      dispatch(
        OthersAction.uploadImage(data, (res) => {
          const fileLink = res.data;
          props?.onChange(fileLink);

          if (fileSelect.current) {
            fileSelect.current.value = "";
          }
        })
      );
    } else {
      data.append("file", file);
      dispatch(
        OthersAction.uploadFile(data, (res) => {
          const fileLink = res.data;
          props?.onChange(fileLink);

          if (fileSelect.current) {
            fileSelect.current.value = "";
          }
        })
      );
    }
  };

  const handleImageUpload = (file) => {
    if (showcrop) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setImageSrc(reader.result);
        setCropModalOpen(true);
      };
    } else {
      handleFileUpload(file);
    }
  };

  const handleCropComplete = async (event) => {
    event?.preventDefault();
    if (!croppedAreaPixels) return;

    const originalFileName = fileSelect?.current?.files[0]?.name;

    try {
      const croppedImage = await Utils.getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        originalFileName
      );

      if (!(croppedImage instanceof File)) {
        console.error("Invalid cropped image format:", croppedImage);
        return;
      }

      setCropModalOpen(false);

      setTimeout(() => {
        if (fileSelect.current) {
          fileSelect.current.blur();
        }
      }, 100);

      handleFileUpload(croppedImage);
    } catch (error) {
      console.error("Error during cropping:", error);
    }
  };

  return (
    <div
      onClick={(e) => {
        if (!cropModalOpen) {
          fileSelect.current.click();
        }
      }}
      className="default-file-input"
    >
      <input
        style={{ display: "none" }}
        type="file"
        name="image"
        id={`getFile-${id}`}
        ref={fileSelect}
        onChange={(event) => {
          handleImageUpload(event.target.files[0]);
        }}
        disabled={disabled || isUploadLoading}
        accept={
          type === "image"
            ? ".jpg, .jpeg, .png"
            : ".pdf, .csv, .xlsx, .doc, .docx"
        }
      />
      <div
        className={`${
          value ? "opacity-[100%]" : "opacity-[50%]"
        } text-[0.875em]`}
      >
        {value ? "File uploaded" : "Select a file"}
      </div>
      <div>
        {isUploadLoading ? (
          <Loader.Circular size={22} />
        ) : (
          <img src={icon} className="h-[1.125em] pr-2" />
        )}
      </div>

      {/* Show Cropper Modal only if showcrop is true */}
      {showcrop && (
        <Modal.DefaultModal
          open={cropModalOpen}
          onCancel={() => setCropModalOpen(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="relative w-[500px] h-[400px] m-6"
            onClick={(e) => e.stopPropagation()}
          >
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, croppedAreaPixels) =>
                setCroppedAreaPixels(croppedAreaPixels)
              }
            />
          </div>
          <div className="flex justify-end gap-3 mt-3">
            <Buttons.WhiteButton
              size="medium"
              onClick={() => setCropModalOpen(false)}
            >
              Cancel
            </Buttons.WhiteButton>
            <Buttons.DefaultButton
              size="medium"
              onClick={(event) => {
                event.stopPropagation(); // Stop click event from bubbling
                handleCropComplete(event);
              }}
            >
              Crop & Upload
            </Buttons.DefaultButton>
          </div>
        </Modal.DefaultModal>
      )}
    </div>
  );
};

export default DefaultFileUpload;
